import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    applicationId: Number,
    // The object this connection is for (e.g. CharityCareApplication + id). Sent to create_session so the
    // server can pick the Stripe environment (demo partner -> test). Generic so future contexts (a Bill,
    // Claim, ...) just set a different type/id without any JS change.
    contextType: String,
    contextId: String
  }

  async connect() {
    const session = await this.createSession()
    if (session?.client_secret) {
      this.clientSecret = session.client_secret
      this.sessionId = session.session_id
      this.publishableKey = session.publishable_key
    }
  }

  async createSession() {
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      
      const response = await fetch('/api/v1/stripe_financial_connections/create_session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          context_type: this.contextTypeValue,
          context_id: this.contextIdValue
        })
      })
      
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to create session: ${response.status} ${errorText}`)
      }
      
      const sessionData = await response.json()
      return sessionData
    } catch (error) {
      this.showError('Failed to initialize connection. Please try again.')
      return null
    }
  }

  async connectBank() {
    console.log('🏦 Connect bank button clicked!')
    console.log('🔑 Client secret available:', !!this.clientSecret)
    console.log('🆔 Session ID:', this.sessionId)
    
    if (!this.clientSecret) {
      const session = await this.createSession()
      if (session?.client_secret) {
        this.clientSecret = session.client_secret
        this.sessionId = session.session_id
        this.publishableKey = session.publishable_key
      } else {
        this.showError('Connection not initialized. Please refresh the page.')
        return
      }
    }

    try {
      
      // Load Stripe SDK
      if (!window.Stripe) {
        await this.loadStripeScript()
      }

      const stripe = window.Stripe(this.publishableKey)
      
      const result = await stripe.collectFinancialConnectionsAccounts({
        clientSecret: this.clientSecret
      })
      
      const session = result.financialConnectionsSession
      
      // Check if accounts were successfully connected (status may be undefined but accounts exist)
      if (session?.accounts && session.accounts.length > 0) {
        await this.completeSession()
      } else if (session?.status === 'succeeded') {
        await this.completeSession()
      } else if (session?.status) {
        this.showError(`Connection status: ${session.status}. Please try again.`)
      } else {
        this.showError('Connection was not completed successfully. Please try again.')
      }
    } catch (error) {
      this.showError('Failed to connect bank account. Please try again.')
    }
  }

  async completeSession() {
    
    try {
      const response = await fetch(`/api/v1/stripe_financial_connections/complete_session/${this.sessionId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          charity_care_application_id: this.applicationIdValue
        })
      })
      
      
      const result = await response.json()
      
      if (result.success) {
        this.showSuccess(`Successfully connected ${result.accounts_count} account(s)! Bank statements are being generated automatically.`)
        this.updateButtonText()
        // Let the express UI swap the connect CTA for a "we're pulling your data" message.
        this.dispatch('connected', { bubbles: true })
        // No need to refresh page anymore since we removed the sections
      } else {
        this.showError(result.error || 'Failed to complete connection.')
      }
    } catch (error) {
      this.showError('Failed to complete connection. Please try again.')
    }
  }

  async loadStripeScript() {
    console.log('📚 Loading Stripe Financial Connections SDK...')
    
    return new Promise((resolve, reject) => {
      if (window.Stripe || document.querySelector('script[src*="js.stripe.com/v3"]')) {
        resolve()
        return
      }
      
      const script = document.createElement('script')
      // Use the standard Stripe.js v3 SDK which includes Financial Connections
      script.src = 'https://js.stripe.com/v3/'
      script.onload = () => {
        resolve()
      }
      script.onerror = (error) => {
        reject(error)
      }
      document.head.appendChild(script)
    })
  }

  showError(message) {
    // Use toast notification instead of alert
    this.showToast(message, 'error')
  }

  showSuccess(message) {
    // Use toast notification instead of alert
    this.showToast(message, 'success')
  }

  showToast(message, type = 'info') {
    // Create and show a toast notification
    const toast = document.createElement('div')
    toast.className = `fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm transform transition-all duration-300 ${
      type === 'error' ? 'bg-red-500 text-white' : 
      type === 'success' ? 'bg-green-500 text-white' : 
      'bg-blue-500 text-white'
    }`
    toast.textContent = message
    
    document.body.appendChild(toast)
    
    // Animate in
    setTimeout(() => toast.classList.add('translate-x-0'), 10)
    
    // Remove after 4 seconds
    setTimeout(() => {
      toast.classList.add('translate-x-full', 'opacity-0')
      setTimeout(() => document.body.removeChild(toast), 300)
    }, 4000)
  }

  updateButtonText() {
    // Update button text to indicate additional connections
    const button = this.element.querySelector('button')
    if (button && button.textContent.trim() === 'Connect Bank Account') {
      button.textContent = 'Connect Another Bank Account'
    }
  }
}